/**
 * The type scale inside `<main>`: one class string per text role, each taken from the HyperTek prototype
 * (h-prototype) rather than chosen here. A screen names the role its text plays — a card's title, a field's
 * hint, a table's header — and never picks a size of its own. That is the whole cure for "chỗ to chỗ bé": two
 * pieces of text that do the same job are the same size because they read the same constant.
 *
 * Where each value comes from (prototype source · rendered value):
 * - `pageTitle` — the Workplace mockup's toolbar title · 18 px/800/26 px (the compact wave, not the prototype's 26/600).
 * - `pageSubtitle` — components/layout/page-header.tsx · `text-sm` (13 px)/450/1.375.
 * - `recordTitle` — a record page's own title under the banner (`DetailHeader`, PO 2026-09-27) · 24 px/800/32 px.
 * - `sectionTitle` — the dashboard's featured-card `h3` (`font-semibold` on the body size) · 14 px/600/1.5.
 *   CardTitle's `leading-none` is not used for it: a product title wraps at 360 px, and a 1.0 line box stacks
 *   Vietnamese diacritics into the line above.
 * - `body`, `muted` — CardDescription and the dashboard's paragraphs (`text-sm`) · 13 px/450/18 px.
 * - `tableHead`, `primary` — ui/table.tsx `TableHead` and the dashboard table's first cell · 13 px/500.
 * - `small` — the dashboard's progress figure (`text-xs` muted) · 12 px/450/16 px.
 * - `kpi` — the dashboard's stat tile value (`text-2xl font-bold`) · 22 px/700/28 px.
 * - `dialogTitle` — ui/dialog.tsx `DialogTitle` · 16 px/600/1.0.
 * - `noticeTitle` — the heading of a status panel (a refused save, a conflict, a confirm-in-place). The prototype's
 *   banners carry no heading, so it is the banner's own `text-sm` with the weight a heading needs · 13 px/600.
 * Labels, inputs, buttons, badges and table cells take theirs from the ui primitives, which are the
 * prototype's own.
 */
export const TEXT = {
  pageTitle:
    'text-[1.125rem] font-extrabold leading-[1.625rem] tracking-tight text-[hsl(var(--h-foreground))]',
  pageSubtitle: 'text-sm leading-snug text-[hsl(var(--h-muted-foreground))]',
  recordTitle:
    'text-[1.5rem] font-extrabold leading-[2rem] tracking-tight text-[hsl(var(--h-foreground))] wrap-anywhere',
  sectionTitle:
    'text-[length:var(--h-font-size-base)] font-semibold leading-normal text-[hsl(var(--h-card-foreground))]',
  body: 'text-sm',
  muted: 'text-sm text-[hsl(var(--h-muted-foreground))]',
  /** A field's label when it is not a `<Label>` (a legend, a group's caption): the Label primitive's classes. */
  label: 'text-sm font-medium leading-none',
  /**
   * The words beside a checkbox: the label's 13 px / 500 on a 20 px line box (`text-sm`'s own). Unlike a
   * `<Label>` over a field, these wrap — a long role name in the role checklist does at 360 px — and the label's
   * line-height 1 would stack each line's Vietnamese diacritics into the line above. The 44 px target is the
   * row's `min-h-11`, not the text's.
   */
  checkLabel: 'text-sm font-medium leading-5',
  hint: 'text-sm text-[hsl(var(--h-muted-foreground))]',
  fieldError: 'text-sm font-medium text-[hsl(var(--h-error-fg))]',
  tableHead: 'text-sm font-medium text-[hsl(var(--h-muted-foreground))]',
  /** The first, naming cell of a row — a table's or a list's. */
  primary: 'text-sm font-medium',
  small: 'text-xs text-[hsl(var(--h-muted-foreground))]',
  kpi: 'text-2xl font-bold tracking-tight',
  dialogTitle: 'text-lg font-semibold leading-none tracking-tight',
  noticeTitle: 'text-sm font-semibold',
} as const;
